/**
 * Коды, на которые реагирует сам api-слой и оболочка приложения.
 * Доменные коды с особой реакцией живут в `entities/<name>/model/errors.ts`.
 */
export const API_ERROR_CODE = {
  /** Заголовка с подписью нет. */
  TG_MISSING: 'TG-001',
  /** Подпись повреждена. */
  TG_MALFORMED: 'TG-002',
  /** Подпись не сошлась: инцидент или у бэкенда другой токен бота. */
  TG_INVALID_SIGNATURE: 'TG-003',
  /** Подписи больше суток — помогает только перезапуск Mini App. */
  TG_EXPIRED: 'TG-004',
  /** В подписи нет пользователя. */
  TG_USER_MISSING: 'TG-005',
  /**
   * Пользователь заблокирован: 403 на любом запросе `/app/*` — бэкенд проверяет
   * блокировку в middleware авторизации, сразу после подписи. Оболочка
   * показывает «Аккаунт заблокирован» вместо всех экранов.
   */
  USER_BLOCKED: 'U-003',
} as const

/** Ответ бэкенда с `success: false` или HTTP-ошибка без конверта. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly data: unknown
  readonly traceId: string

  constructor(params: { status: number; code: string; message: string; data?: unknown; traceId: string }) {
    super(params.message)
    this.name = 'ApiError'
    this.status = params.status
    this.code = params.code
    this.data = params.data ?? null
    this.traceId = params.traceId
  }
}

/** Запрос не дошёл до бэкенда или ответ не пришёл. Единственный вид ошибки, который имеет смысл повторять. */
export class NetworkError extends Error {
  readonly traceId: string
  /** Исходный сбой `fetch`. Своим полем, а не `Error(…, { cause })`: опции конструктора — с Chrome 93 / iOS 15. */
  readonly cause: unknown

  constructor(traceId: string, cause?: unknown) {
    super('Network request failed')
    this.name = 'NetworkError'
    this.traceId = traceId
    this.cause = cause
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError

export const isNetworkError = (error: unknown): error is NetworkError => error instanceof NetworkError

const TELEGRAM_AUTH_CODES: ReadonlySet<string> = new Set([
  API_ERROR_CODE.TG_MISSING,
  API_ERROR_CODE.TG_MALFORMED,
  API_ERROR_CODE.TG_INVALID_SIGNATURE,
  API_ERROR_CODE.TG_EXPIRED,
  API_ERROR_CODE.TG_USER_MISSING,
])

/**
 * Бэкенд не принял подпись Telegram. Касается всего приложения сразу: та же
 * `initData` уходит в каждом запросе и до перезапуска Mini App не меняется,
 * поэтому повторять такой запрос автоматически нельзя — будет цикл.
 */
export const isTelegramAuthError = (error: unknown): error is ApiError =>
  isApiError(error) && TELEGRAM_AUTH_CODES.has(error.code)

/**
 * Пользователь заблокирован (`U-003`). Как и отказ подписи, касается всего
 * приложения: бэкенд отвечает так на любой запрос `/app/*`, и повтор не
 * поможет — снимает блокировку только поддержка.
 */
export const isUserBlockedError = (error: unknown): error is ApiError =>
  isApiError(error) && error.code === API_ERROR_CODE.USER_BLOCKED

/** trace-id любой ошибки api-слоя — для экрана ошибки и обращения в поддержку. */
export const getTraceId = (error: unknown): string | null =>
  isApiError(error) || isNetworkError(error) ? error.traceId : null
